import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import defaultOnErrorHandler from '../../../../shared/default-on-error-handler';
import Loading from '../../../../shared/loading';
import {
  Section,
  SectionDetailLabel,
  SectionDetails,
  SectionSeparator,
  SectionSummary,
  SectionSummaryDescription,
  SectionSummaryTitle,
} from './styles';
import { postToggleAreaOfExpertise } from '../../../../services/profiles/contributor-settings-service';

function getAreasForDisplay(areas) {
  const parentsOf = area => _(areas).filter(n => area.parents.indexOf(n.slug) !== -1);
  const childrenOf = area => _(areas).filter(n => n.parents.indexOf(area.slug) !== -1);
  const siblingsOf = area => parentsOf(area)
    .map(n => childrenOf(n).value())
    .uniqBy(n => n.slug)
    .flatten();
  const descendantsOf = area => childrenOf(area)
    .reduce((acc, node) => acc.concat(node, descendantsOf(node).value()), _([]))
    .flatten();
  const areaHasNoParents = area => area.parents.length === 0;
  const areaHasAssignedSiblings = area => siblingsOf(area).some(n => n.assignedToMe);
  const areaHasAssignedDescendants = area => descendantsOf(area).some(n => n.assignedToMe);
  const areaHasAssignedParents = area => parentsOf(area).some(n => n.assignedToMe);
  const shouldDisplay = area => areaHasNoParents(area)
    || areaHasAssignedSiblings(area)
    || areaHasAssignedDescendants(area)
    || areaHasAssignedParents(area);

  return areas.filter(shouldDisplay);
}

const Area = ({
  name, parents, assignedToMe, clickHandler,
}) => {
  const classes = classNames('btn', {
    'btn-primary': assignedToMe,
    'btn-secondary-inverted': !assignedToMe && !parents.length,
    'btn-tertiary': !assignedToMe && parents.length,
  });

  return (
    <li>
      <button type="button" className={classes} onClick={clickHandler}>{name}</button>
    </li>
  );
};

Area.propTypes = {
  name: PropTypes.string.isRequired,
  parents: PropTypes.arrayOf(PropTypes.string).isRequired,
  assignedToMe: PropTypes.bool.isRequired,
  clickHandler: PropTypes.func.isRequired,
};

const MAX = 5;

export default function Expertise({ areasOfExpertise, onUpdate }) {
  const [hasError, setHasError] = React.useState(false);
  const [areas, setAreas] = React.useState([]);
  const loadingRef = React.useRef();

  React.useEffect(() => setAreas(areasOfExpertise), [areasOfExpertise]);

  const toggle = area => {
    const count = areas.filter(x => x.assignedToMe).length;
    if (!area.assignedToMe && count === MAX) {
      setHasError(true);
      return;
    }

    loadingRef.current.start();
    postToggleAreaOfExpertise(area)
      .then(() => {
        setAreas(oldAreas => {
          const newAreas = oldAreas.map(a => {
            if (a.slug === area.slug) {
              return { ...a, assignedToMe: !a.assignedToMe };
            }
            return { ...a };
          });
          onUpdate({ areasOfExpertise: newAreas.filter(a => a.assignedToMe) });
          return newAreas;
        });
        setHasError(false);
      })
      .catch(err => defaultOnErrorHandler(err, 'Error updating assigned areas of expertise'))
      .finally(() => loadingRef.current.stop());
  };

  return (
    <>
      <SectionSeparator />
      <Section className={classNames('form-group', { 'has-error': hasError })}>
        <SectionSummary>
          <SectionSummaryTitle>Expertise</SectionSummaryTitle>
          <SectionSummaryDescription>
            Select five expertise categories which best describe your knowledge
            and your fund, research or analysis strategy.
          </SectionSummaryDescription>
        </SectionSummary>
        <SectionDetails>
          <SectionDetailLabel>
            Your investment expertise
            {!!hasError && (
              <p className="help-block">You can select at most five expertise categories</p>
            )}
          </SectionDetailLabel>
          <ul className="btn-cloud">
            {getAreasForDisplay(areas).map(a => {
              const props = {
                ...a,
                key: a.slug,
                clickHandler: () => toggle(a),
              };
              return <Area {...props} />;
            })}
          </ul>
          <p className="sip-controls">
            <Loading variant="sip" ref={loadingRef} />
          </p>
        </SectionDetails>
      </Section>
    </>
  );
}

Expertise.propTypes = {
  areasOfExpertise: PropTypes.arrayOf(PropTypes.shape({
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    parents: PropTypes.arrayOf(PropTypes.string).isRequired,
    assignedToMe: PropTypes.bool.isRequired,
  })).isRequired,
  onUpdate: PropTypes.func,
};

Expertise.defaultProps = { onUpdate: () => null };
